import { fetchWithAuth, BASE_URL } from './client';

/**
 * Fetch team members for the current user
 * GET /teams/members
 */
export async function fetchTeamMembers() {
    const response = await fetchWithAuth(`${BASE_URL}/api/teams/members`);
    // Gracefully handle 404 - endpoint may not exist yet
    if (response.status === 404) {
        return { data: { members: [] } };
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch team members: ${response.statusText}`);
    }
    // Handle empty response body
    try {
        return await response.json();
    } catch {
        return { data: { members: [] } };
    }
}

/**
 * Invite a user to the team
 * POST /teams/invite
 */
export async function inviteTeamMember(email) {
    const response = await fetchWithAuth(`${BASE_URL}/api/teams/invite`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
    });

    if (!response.ok) {
        // Handle 403 Forbidden specifically for limit reached
        if (response.status === 403) {
            const errorData = await response.json();
            const message = errorData?.detail?.message || "Team member limit reached for your plan.";
            throw new Error(message);
        }
        throw new Error(`Failed to invite member: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Remove a team member
 * DELETE /teams/members/{memberId}
 */
export async function removeTeamMember(memberId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/teams/members/${memberId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        throw new Error(`Failed to remove team member: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Get members assigned to a specific case
 * GET /cases/{caseId}/team-access
 */
export async function fetchCaseTeamAccess(caseId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/team-access`);
    // Gracefully handle 404 - endpoint may not exist yet
    if (response.status === 404) {
        return { data: { assignedMembers: [] } };
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch case access: ${response.statusText}`);
    }
    // Handle empty response body
    try {
        return await response.json();
    } catch {
        return { data: { assignedMembers: [] } };
    }
}

/**
 * Assign a team member to a case
 * POST /cases/{caseId}/team-access
 */
export async function assignMemberToCase(caseId, memberId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/team-access`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ memberId })
    });
    if (!response.ok) {
        throw new Error(`Failed to assign member: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Remove a team member from a case
 * DELETE /cases/{caseId}/team-access/{memberId}
 */
export async function removeMemberFromCase(caseId, memberId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/team-access/${memberId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        throw new Error(`Failed to remove member access: ${response.statusText}`);
    }
    return response.json();
}
